import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Link } from 'react-router-dom';

import debounce from 'lodash/debounce';
import { length } from 'stringz';

import { clearComposeAll } from 'mastodon/actions/compose';
import { openModal } from 'mastodon/actions/modal';
import { Avatar } from 'mastodon/components/avatar';
import { Button } from 'mastodon/components/button';
import { IconButton } from 'mastodon/components/icon_button';
import { CloseIcon } from 'mastodon/components/icons/close';
import { ImageIcon } from 'mastodon/components/icons/image';
import { Skeleton } from 'mastodon/components/skeleton';
import { useSidebar } from 'mastodon/components/use-sidebar';
import { me } from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import ComposeFormContainer from '../containers/compose_form_container';
import { countableText } from '../util/counter';

const messages = defineMessages({
  placeholder: { id: 'compose_form.placeholder', defaultMessage: "What's on your mind?" },
  placeholderProfile: { id: 'compose_form.profile.placeholder', defaultMessage: 'How was your day today?' },
  create_post: { id: 'status.create_post', defaultMessage: 'Create post' },
  close: { id: 'bundle_modal_error.close', defaultMessage: 'Close' },
  upload: { id: 'upload_button.label', defaultMessage: 'Add images, a video or an audio file' },
  composeAdd: {
    id: 'compose.add',
    defaultMessage: 'Create a new post'
  }
});

const BOTTOM_SHEET_FORM_ID = 'compose-bottom-sheet-form';

const ComposeFormTrigger = ({ isNavigation }) => {
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const intl = useIntl();
  const bottomSheetRef = useRef(null);
  const contentRef = useRef(null);
  const isProcessingRef = useRef(false);
  const lastEventTypeRef = useRef(null); 
  const account = useAppSelector((state) => state.accounts.get(me));
  const composeText = useAppSelector((state) => state.getIn(['compose', 'text']));
  const composeSpoiler = useAppSelector((state) => state.getIn(['compose', 'spoiler']));
  const composeSpoilerText = useAppSelector((state) => state.getIn(['compose', 'spoiler_text']));
  const composeIsSubmitting = useAppSelector((state) => state.getIn(['compose', 'is_submitting']));
  const composeIsChangingUpload = useAppSelector((state) => state.getIn(['compose', 'is_changing_upload']));
  const composeIsUploading = useAppSelector((state) => state.getIn(['compose', 'is_uploading']));
  const composeAnyMedia = useAppSelector((state) => state.getIn(['compose', 'media_attachments']).size > 0);
  const composeMaxChars = useAppSelector((state) => state.getIn(['server', 'server', 'configuration', 'statuses', 'max_characters'], 500));
  const dispatch = useAppDispatch();

  const handleViewportResize = useCallback(
    debounce(() => {
      const bottomSheet = document.querySelector('.compose-form-bottom-sheet-wrapper');
      if (bottomSheet && window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - viewportHeight;
        const viewportOffsetTop = window.visualViewport.offsetTop || 0;

        if (keyboardHeight > 0) {
          const adjustedBottom = Math.max(keyboardHeight - viewportOffsetTop, 0);
          bottomSheet.style.bottom = `${adjustedBottom}px`;
        } else {
          bottomSheet.style.bottom = 'env(safe-area-inset-bottom, 0px)';
        }
      }
    }, 100),
    []
  );
  
  const { closeSidebar } = useSidebar();

  useEffect(() => {
    const preventBackgroundScroll = (e) => {
      if (showBottomSheet) {
        e.preventDefault();
      }
    };

    if (showBottomSheet) {
      if (bottomSheetRef.current) {
        bottomSheetRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
      window.visualViewport.addEventListener('resize', handleViewportResize);
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.overscrollBehavior = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.overscrollBehavior = '';
      document.body.style.touchAction = '';
      document.removeEventListener('touchmove', preventBackgroundScroll);
      window.visualViewport.removeEventListener('resize', handleViewportResize);
      handleViewportResize.cancel();
    };
  }, [showBottomSheet, handleViewportResize]);

  useEffect(() => {
    const handleShowBottomSheet = () => {
      setShowBottomSheet(true);
    };

    window.addEventListener('showBottomSheet', handleShowBottomSheet);
    return () => {
      window.removeEventListener('showBottomSheet', handleShowBottomSheet);
    };
  }, []);


  const handleInputClick = useCallback(
    (e) => {
      if (isProcessingRef.current) return;

      isProcessingRef.current = true;
      lastEventTypeRef.current = e.type;

      try {
        e.preventDefault();
        closeSidebar();
        dispatch(clearComposeAll());
        setTimeout(() => {
          if (window.innerWidth < 768) {
            setShowBottomSheet(true);
          } else {
            dispatch(
              openModal({
                modalType: 'COMPOSE_FORM',
                modalProps: {},
              })
            );
          }
        }, 150);
      } finally {
        setTimeout(() => {
          isProcessingRef.current = false;
          lastEventTypeRef.current = null;
        }, 100);
      }
    },
    [dispatch, closeSidebar]
  );

  const handleWrapperClick = useCallback((e) => {
    if (e.target.classList.contains('compose-form-bottom-sheet-wrapper')) {
      setShowBottomSheet(false);
    }
  }, []);

  const handleBottomSheetClose = useCallback(() => {
    setShowBottomSheet(false);
  }, []);

  const getPlaceholder = () => {
    const isProfilePage = window.location.pathname.match(/^\/@[\w]+$/);
    return isProfilePage ? messages.placeholderProfile : messages.placeholder;
  };

  const handleCloseCompose = useCallback(() => {
    setShowBottomSheet(false);
    dispatch(clearComposeAll());
  }, [dispatch]);

  const fulltext = [composeSpoiler ? composeSpoilerText : '', countableText(composeText)].join('');
  const isComposeOnlyWhitespace = fulltext.trim().length === 0;
  const canSubmitCompose = !(
    composeIsSubmitting ||
    composeIsUploading ||
    composeIsChangingUpload ||
    length(fulltext) > composeMaxChars ||
    (isComposeOnlyWhitespace && !composeAnyMedia) ||
    (!fulltext && !composeAnyMedia)
  );

  const bottomSheet = (
    <div
      className='compose-form-bottom-sheet-wrapper'
      onClick={handleWrapperClick}
      role='presentation'
      style={{
        position: 'fixed',
        bottom: 'env(safe-area-inset-bottom, 0px)',
        left: 0,
        right: 0,
        top: 0,
        zIndex: 1000,
      }}
    >
      <div className='compose-form-bottom-sheet' ref={bottomSheetRef}>
        <div className='compose-form-bottom-sheet__header'>
          <IconButton
            className='sync-modal__close'
            title={intl.formatMessage(messages.close)}
            icon='close'
            iconComponent={CloseIcon}
            onClick={handleCloseCompose}
            size={20}
          />
          <h3>{intl.formatMessage(messages.create_post)}</h3>
          <Button
            type='submit'
            form={BOTTOM_SHEET_FORM_ID}
            compact
            className='compose-form-bottom-sheet__publish'
            text={<FormattedMessage id='compose_form.publish' defaultMessage='Post' />}
            disabled={!canSubmitCompose}
          />
        </div>
        <div className='compose-form-bottom-sheet__content' ref={contentRef}>
          <ComposeFormContainer autoFocus onClose={handleBottomSheetClose} formId={BOTTOM_SHEET_FORM_ID} />
        </div>
      </div>
    </div>
  );

  return (
    <div className='compose-form-trigger'>
      <div className='compose-form-trigger__content'>
        {!isNavigation &&
          <Link
            className='account__display-name'
            title={account?.acct}
            to={`/@${account?.acct}`}
            data-hover-card-account={me}
          >
            <div className='account__avatar-wrapper'>
              {account ? (
                <Avatar account={account} size={36} />
              ) : (
                <Skeleton width={36} height={36} />
              )}
            </div>
          </Link>
        }
      
        {isNavigation ? (
          <Button
            text={intl.formatMessage(messages.composeAdd)}
            onClick={handleInputClick}
            className='navigation-create-button'
          />
        ) : (
          <div
            className='compose-form-trigger__input'
            onClick={handleInputClick}
            style={{ touchAction: 'manipulation', userSelect: 'none' }}
            role='button'
            tabIndex={0}
          >
            <FormattedMessage {...getPlaceholder()} />
          </div>
        )}
       

        {window.innerWidth < 768 && !isNavigation && (
          <IconButton
            icon='paperclip'
            iconComponent={ImageIcon}
            title={intl.formatMessage(messages.upload)}
            onClick={handleInputClick}
            className='compose-form__upload-button-icon'
            size={18}
            inverted
            style={{ height: null, lineHeight: '27px' }}
          />
        )}
      </div>

      {showBottomSheet && createPortal(bottomSheet, document.body)}
    </div>
  );
};

ComposeFormTrigger.propTypes = {
  isNavigation: PropTypes.bool,
};

ComposeFormTrigger.defaultProps = {
  isNavigation: false,
};

export default ComposeFormTrigger;
